import {
  Dialog,
  Button,
  Typography,
  Stack,
  TextField,
  Autocomplete,
} from '@mui/material';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import { useGetActiveOnlyShift } from '@api/pos/pos-work-period.api.rtk';
import ContainerTitle from '@components/ui/container-title';
import { GroupRemoveOutlined } from '@mui/icons-material';
import FOCashBreakdownDrawer from '@modules/front-office/components/fo-cash-breakdown.drawer';
import { useEffect, useState } from 'react';
import useUser from '@hooks/useUser';
import { selectPOSstationID } from '@modules/point-of-sale/slice/pos-selector';
import { useAppSelector } from '@store/index';

export interface IForceEndShift {
  modalDisclosure: UseDisclosureInterface;
}

export interface IShiftData {
  stationRefID: string;
  stationName: string;
  subModuleCode: string;
  shiftRefID: string;
  workPeriodID: string;
  workPeriod: string;
  startedByUserID: string;
  endedByUserID: string;
  statedBy: string;
  endedBy: string;
  salesDate: string;
}

/**
 * ForceEndShiftModal
 * Confirms the end-of-shift cash reconciliation before closing out a POS session.
 * Displays a read-only summary of sales for the shift, and asks the cashier
 * to enter the physically counted cash so a variance can be calculated.
 */
const ForceEndShiftModal = ({ modalDisclosure }: IForceEndShift) => {
  const [selectedShift, setSelectedShift] = useState<IShiftData | null>(null);
  const cashBreakdownDisclosure = useDisclosure();
  const stationID = useAppSelector(selectPOSstationID);

  const { data } = useGetActiveOnlyShift(
    { isActiveOnly: 1, stationID },
    { refetchOnMountOrArgChange: true },
  );

  const userShiftData = data?.results;
  const userData = useUser();

  useEffect(() => {
    if (!modalDisclosure.isOpen) {
      cashBreakdownDisclosure.onClose();
    }
  }, [modalDisclosure.isOpen, cashBreakdownDisclosure]);

  const handleSelectCashier = () => {
    if (!selectedShift) return;
    cashBreakdownDisclosure.onOpen();
  };

  const handleClose = () => {
    modalDisclosure.onClose();
  };

  const handleCloseAll = () => {
    setSelectedShift(null);
    cashBreakdownDisclosure.onClose();
    modalDisclosure.onClose();
  };

  return (
    <>
      <Dialog
        open={modalDisclosure.isOpen}
        onClose={handleClose}
        sx={{
          display: cashBreakdownDisclosure.isOpen ? 'none' : 'block',
        }}
      >
        <Stack
          sx={{ width: '550px', maxWidth: '100%', height: '100%' }}
          gap={2}
          p={3}
        >
          <Stack
            sx={{
              bgcolor: 'primary.light',
              borderRadius: 100,
              height: 40,
              width: 40,
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
            }}
          >
            <GroupRemoveOutlined sx={{ height: 32, aspectRatio: 1 }} />
          </Stack>
          <ContainerTitle
            title='Force End Shift'
            titleStyle={{ fontSize: '20px!important' }}
          />
          <Typography sx={{ opacity: 0.5 }}>
            Please select Cashier you want to end shift.
          </Typography>
          <Stack height='100%' pt={1} flex={1} sx={{ overflowY: 'auto' }}>
            <Autocomplete
              options={userShiftData || []}
              getOptionLabel={(option) => option?.statedBy}
              onChange={(_, value) => {
                setSelectedShift(value || null);
              }}
              getOptionDisabled={(option) => {
                return option?.startedByUserID === userData?.userID;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Cashier'
                  placeholder='Select Cashier'
                  autoFocus
                />
              )}
            />
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            gap={2}
          >
            <Button variant='outlined' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSelectCashier}
              variant='contained'
              disabled={!selectedShift}
            >
              Select
            </Button>
          </Stack>
        </Stack>
      </Dialog>
      <FOCashBreakdownDrawer
        drawerDisclosure={{
          ...cashBreakdownDisclosure,
          onClose: handleCloseAll,
        }}
        transactionType='force-end-shift'
        selectedShift={selectedShift}
      />
    </>
  );
};

export default ForceEndShiftModal;
